import React from 'react'
import {
  Modal, ModalOverlay, ModalContent, ModalBody,
  Box, VStack, Text, Image,
} from '@chakra-ui/react'
import { motion, useReducedMotion } from 'framer-motion'
import { sheetVariants, containerVariants, itemVariants, imageVariants } from './animations'
import CloseButton from './CloseButton'
import AudioBar from './AudioBar'

const MotionContent = motion(ModalContent)

const ArtworkSheet = ({ isOpen, onClose, artwork }) => {
  const prefersReduced = useReducedMotion()
  const imgSrc = artwork?.uri ? `${process.env.REACT_APP_API_URL}${artwork.uri}` : null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      motionPreset="none"
      size="full"
      scrollBehavior="inside"
      isCentered={false}
    >
      <ModalOverlay
        bg="rgba(26,29,36,0.75)"
        as={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: prefersReduced ? 0.1 : 0.3 } }}
        exit={{ opacity: 0, transition: { duration: prefersReduced ? 0.1 : 0.25 } }}
      />
      <MotionContent
        variants={sheetVariants(prefersReduced)}
        initial="hidden"
        animate="visible"
        exit="exit"
        drag={prefersReduced ? false : 'y'}
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={{ top: 0, bottom: 0.5 }}
        onDragEnd={(_, info) => {
          if (info.offset.y > 120 || info.velocity.y > 500) onClose()
        }}
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        top="auto"
        m={0}
        bg="lumene.white"
        color="lumene.night"
        borderTopRadius="24px"
        borderBottomRadius={0}
        maxH="88vh"
        overflowY="auto"
        sx={{ overscrollBehavior: 'contain' }}
        pt="14px"
        pb="calc(28px + env(safe-area-inset-bottom, 0px))"
        px={5}
        boxShadow="0 -8px 24px rgba(0,0,0,0.2)"
        aria-labelledby="artwork-title"
      >
        {/* Drag handle */}
        <Box
          w="40px" h="4px"
          bg="lumene.night"
          opacity={0.2}
          borderRadius="full"
          mx="auto"
          mb={4}
          aria-hidden="true"
        />

        <CloseButton onClick={onClose} position="absolute" top="10px" right="10px" />

        <ModalBody p={0}>
          <motion.div
            variants={containerVariants(prefersReduced)}
            initial="hidden"
            animate="visible"
          >
            <VStack spacing={4} align="stretch">
              {imgSrc && (
                <motion.div variants={imageVariants}>
                  <Image
                    src={imgSrc}
                    alt={artwork.title}
                    w="100%"
                    h="180px"
                    objectFit="cover"
                    borderRadius="lg"
                    fallbackSrc="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                  />
                </motion.div>
              )}

              {(artwork.year || artwork.artist) && (
                <motion.div variants={itemVariants}>
                  <Text
                    fontSize="xs"
                    color="lumene.bronze"
                    letterSpacing="2px"
                    textTransform="uppercase"
                    fontWeight="bold"
                  >
                    {[artwork.artist, artwork.year].filter(Boolean).join(' · ')}
                  </Text>
                </motion.div>
              )}

              <motion.div variants={itemVariants}>
                <Text
                  as="h2"
                  id="artwork-title"
                  fontSize="22px"
                  fontWeight="bold"
                  color="lumene.night"
                  lineHeight={1.2}
                >
                  {artwork.title}
                </Text>
              </motion.div>

              {artwork.description && (
                <>
                  <motion.div variants={itemVariants}>
                    <Text fontSize="14px" color="lumene.night" lineHeight={1.6}>
                      {artwork.description}
                    </Text>
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <AudioBar description={artwork.description} autoPlay autoPlayDelay={350} />
                  </motion.div>
                </>
              )}
            </VStack>
          </motion.div>
        </ModalBody>
      </MotionContent>
    </Modal>
  )
}

export default ArtworkSheet
